import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

export type PinnedTabLayer = {
  id: string
  node: ReactNode
  className?: string
}

/**
 * Tab content for WorkspaceAgentSideColumn `pinnedFooter`: every layer stays mounted,
 * only the active one is visible and interactive.
 * - fade: layers cover each other (`bodySlot` over the transcript).
 * - stack: layers share one grid cell, the tallest sets the height (footer), so the
 *   tab pill on the footer edge does not move between tabs.
 */
export function PinnedTabLayers({
  active,
  layers,
  mode,
  className,
}: {
  active: string | null
  layers: PinnedTabLayer[]
  mode: "fade" | "stack"
  className?: string
}) {
  return (
    <div className={cn(mode === "fade" ? "relative h-full min-h-0" : "grid h-full grid-rows-1", className)}>
      {layers.map((layer) => {
        const on = layer.id === active
        return (
          <div
            key={layer.id}
            className={cn(
              mode === "fade"
                ? cn(
                    "absolute inset-0 transition-opacity duration-200",
                    on ? "opacity-100" : "pointer-events-none opacity-0",
                  )
                : cn("col-start-1 row-start-1 h-full min-w-0", !on && "pointer-events-none invisible"),
              layer.className,
            )}
            aria-hidden={on ? undefined : true}
            {...(on ? {} : { inert: true })}
          >
            {layer.node}
          </div>
        )
      })}
    </div>
  )
}

/** Footer frame under the tab pill (pt-5 clears the half of the pill that overlaps it). */
export function PinnedFooterBox({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <div className={cn("h-full rounded-lg border border-ui-border bg-ui-surface-sunken px-2.5 pt-5 pb-2.5", className)}>
      {children}
    </div>
  )
}

/**
 * Generation settings row: settings wrap on the left, `trailing` (styles toggle + Generate)
 * stays together on the right edge. Portaled content uses the bare row so the frame
 * (PinnedFooterBox) belongs to the column and never blinks while the content swaps.
 */
export function PinnedGenerationRow({ children, trailing }: { children: ReactNode; trailing: ReactNode }) {
  return (
    <div className="relative z-10 flex min-w-0 flex-wrap items-center gap-2">
      {children}
      <div className="ml-auto flex shrink-0 items-center gap-2">{trailing}</div>
    </div>
  )
}

/** PinnedGenerationRow in its footer frame (canvas). */
export function PinnedGenerationFooter({ children, trailing }: { children: ReactNode; trailing: ReactNode }) {
  return (
    <PinnedFooterBox>
      <PinnedGenerationRow trailing={trailing}>{children}</PinnedGenerationRow>
    </PinnedFooterBox>
  )
}
